/**
 * Request parameters of a picker screen, as the open form answers them.
 *
 * A picker's list and the preview of the record picked from it must reach the
 * backend with the same values: a column such as "free in the task's term?"
 * is computed from them, and the preview would otherwise contradict the list.
 */

/** A form value can be a raw id or a loaded relation {id, ...}; filters need the id. */
export const toFilterValue = (value: any): string | null => {
    const raw = value !== null && typeof value === 'object' && value.id !== undefined ? value.id : value;
    if (raw === undefined || raw === null || raw === '') return null;
    return String(raw);
};

const hasKey = (values: Record<string, any>, key: string) => Object.prototype.hasOwnProperty.call(values, key);

/** Every request key of the picker: its context filters and its query parameters. */
export const pickerFilterCodes = (picker: any): string[] => Array.from(new Set<string>([
    ...((picker?.filters ?? []) as any[]).map(filter => filter.code),
    ...((picker?.queryParameters ?? []) as string[]),
].filter(Boolean)));

/**
 * Picker filter codes the form can answer, with the form's current value
 * (null when the field is empty). A code is answered by the form field of the
 * same code, or by the field whose screen column carries it as its contextCode.
 * Codes the form does not carry are left to the stored context.
 */
export const filtersOwnedByForm = (picker: any, formScreen: any, formValues?: Record<string, any>): Record<string, string | null> => {
    const owned: Record<string, string | null> = {};
    if (!picker || !formValues) return owned;
    const fieldByContextCode = new Map<string, string>();
    (formScreen?.columns ?? []).forEach((column: any) => {
        if (column.contextCode && column.code && !fieldByContextCode.has(column.contextCode)) {
            fieldByContextCode.set(column.contextCode, column.code);
        }
    });
    pickerFilterCodes(picker).forEach((code) => {
        const source = hasKey(formValues, code) ? code : fieldByContextCode.get(code);
        if (!source || !hasKey(formValues, source)) return;
        owned[code] = toFilterValue(formValues[source]);
    });
    return owned;
};

/**
 * The values the picker's list is requested with, for any other request to the
 * same screen: the form's value where the form owns the code (an emptied field
 * sends nothing rather than stale context), the stored context otherwise.
 */
export const resolvePickerRequestFilters = (
    picker: any,
    formScreen: any,
    formValues: Record<string, any> | undefined,
    getFilter: (code: string) => unknown,
): Record<string, string> => {
    const owned = filtersOwnedByForm(picker, formScreen, formValues);
    const resolved: Record<string, string> = {};
    pickerFilterCodes(picker).forEach((code) => {
        if (hasKey(owned, code)) {
            if (owned[code] !== null) resolved[code] = owned[code] as string;
            return;
        }
        const stored = toFilterValue(getFilter(code));
        if (stored !== null) resolved[code] = stored;
    });
    return resolved;
};
